import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/solid";
import { postQuestionVote } from "../../../services/questionService";
import { Button } from "../../../components/forms/Buttons/Button";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canReviewIn } from "../../../utils/folders";
import { getCodedErrorMessage } from "../../../utils/apiError";
import { cardClass, focusRing } from "../../Exams/TestBuilder/ui";
import QuestionCard from "../review/QuestionCard";
import AuthorReviewPanel from "../review/AuthorReviewPanel";
import CommentField from "../review/CommentField";
import StatusHistory from "../review/StatusHistory";
import { COMMENT_LIMIT, cleanComment } from "../review/reviewUtils";

const VOTE_CODES = { UPVOTED: "up", DOWNVOTED: "down" };

// The user's own vote, next to the question's title.
function UserVoteBadge({ evaluation }) {
  const { t } = useTranslation();
  const vote = VOTE_CODES[evaluation?.code];
  if (!vote) return null;
  return vote === "up" ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
      <HandThumbUpIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {t("question_detail.vote.you_upvoted")}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
      <HandThumbDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {t("question_detail.vote.you_downvoted")}
    </span>
  );
}

// The page of those who vote on a question outside folders (vote_question): for or against, with a comment. Main
// reads the question (with the user's own vote) and its history (useQuestionPage) and draws the top bar above;
// `reload` reads them again.
export default function VoterPage({
  question,
  history,
  historyLoading,
  historyFailed,
  reload,
  folderRole = null,
  inFolder = false,
}) {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const commentRef = useRef(null);
  const [selectedVote, setSelectedVote] = useState("");
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [loading, setLoading] = useState(false);

  // In a folder's subject the role decides (expert / moderator / admin); outside folders, the global vote_question.
  const canVote = inFolder ? canReviewIn(folderRole) : user?.perms?.includes("vote_question") || false;
  const evaluation = question.evaluation?.code;

  // The vote the server has for the user is the one chosen.
  useEffect(() => {
    if (VOTE_CODES[evaluation]) setSelectedVote(VOTE_CODES[evaluation]);
  }, [evaluation]);

  const handleVoteSelect = (voteType) => setSelectedVote(selectedVote === voteType ? "" : voteType);

  // The backend takes a vote with an optional comment (never the old "no comment"), up to COMMENT_LIMIT long.
  const handleSubmitVote = async () => {
    if (!selectedVote) return;
    setLoading(true);
    setCommentError("");
    try {
      const text = cleanComment(comment);
      await postQuestionVote(question.uuid, { vote: selectedVote, ...(text ? { comment: text } : {}) });
      toast.success(
        selectedVote === "up" ? t("question_detail.toast.vote_submitted_up") : t("question_detail.toast.vote_submitted_down")
      );
      setComment("");
      reload();
    } catch (err) {
      if (Array.isArray(err?.response?.data?.comment)) {
        setCommentError(t("question_detail.review.comment_too_long", { max: COMMENT_LIMIT }));
        commentRef.current?.focus();
      } else {
        toast.error(getCodedErrorMessage(t, err, t("question_detail.toast.review_failed")));
      }
    } finally {
      setLoading(false);
    }
  };

  const voteButton = (vote, Icon, label, chosen, idle) => (
    <button
      type="button"
      onClick={() => handleVoteSelect(vote)}
      aria-pressed={selectedVote === vote}
      className={`inline-flex h-11 items-center gap-2 rounded-lg px-5 text-sm ring-1 ring-inset transition duration-200 active:scale-[0.98] ${focusRing} ${
        selectedVote === vote ? chosen : idle
      }`}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
      <span className="font-medium">{label}</span>
    </button>
  );

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 motion-safe:animate-fade-in 2xl:max-w-screen-2xl">
      <div className="grid grid-cols-1 items-start gap-6 2xl:grid-cols-2">
        <QuestionCard
          question={question}
          showVotes
          titleExtra={canVote ? <UserVoteBadge evaluation={question.evaluation} /> : null}
        />

        <div className="space-y-6">
          <AuthorReviewPanel question={question} history={history} onChanged={reload} onReload={reload} />

          <section aria-labelledby="vote-title" className={cardClass}>
            <div className="p-5 sm:p-6">
              <h3 id="vote-title" className="mb-4 text-lg font-semibold tracking-tight text-slate-900">
                {t("question_detail.vote.title")}
              </h3>

              <div role="group" aria-labelledby="vote-title" className="mb-6 flex flex-wrap gap-3">
                {voteButton(
                  "up",
                  HandThumbUpIcon,
                  t("question_detail.vote.upvote"),
                  "bg-emerald-600 text-white ring-emerald-600 shadow-sm shadow-emerald-600/25",
                  "bg-white text-emerald-700 ring-emerald-200 hover:bg-emerald-50 hover:ring-emerald-300"
                )}
                {voteButton(
                  "down",
                  HandThumbDownIcon,
                  t("question_detail.vote.downvote"),
                  "bg-red-600 text-white ring-red-600 shadow-sm shadow-red-600/25",
                  "bg-white text-red-700 ring-red-200 hover:bg-red-50 hover:ring-red-300"
                )}
              </div>

              <CommentField
                ref={commentRef}
                id="voteComment"
                label={t("question_detail.comment.label")}
                placeholder={t("question_detail.comment.vote_placeholder")}
                value={comment}
                onChange={(value) => {
                  setComment(value);
                  setCommentError("");
                }}
                error={commentError}
              />

              <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
                <Button
                  text={t("question_detail.vote.submit")}
                  color="primary"
                  loading={loading}
                  onClick={handleSubmitVote}
                  disabled={!selectedVote}
                  className="max-md:h-11"
                />
              </div>
            </div>
          </section>

          <StatusHistory
            history={history}
            loading={historyLoading}
            failed={historyFailed}
            authorUuid={question.author?.uuid}
          />
        </div>
      </div>
    </div>
  );
}
